import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { AlertTriangle } from "lucide-react"; // Lucide icon
import { login } from "../../store/authSlice";
import { Button } from "../../components/js-components/button";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      console.log("Login Attempt:", { 
        username, 
        apiBaseUrl: API_BASE_URL 
      });
  
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });
  
      console.log("Full Login Response:", response.data);
  
      // Check for authorization
      if (!response.data.isAuthorized) {
        console.error("Authorization Failed:", response.data);
        setError(response.data.message || "Not authorized. Wait for an admin to assign your role.");
        setLoading(false);
        return;
      }
  
      // Detailed login dispatch logging
      console.log("Dispatching Login Action with:", {
        id: response.data.user.id,
        username: response.data.user.username,
        role: response.data.user.role,
        locationid: response.data.user.locationid,
        isAuthorized: response.data.isAuthorized
      });
  
      localStorage.setItem("token", response.data.token);
  
      dispatch(
        login({
          id: response.data.user.id,
          username: response.data.user.username,
          role: response.data.user.role,
          locationid: response.data.user.locationid,
          isAuthorized: response.data.isAuthorized
        })
      );
  
      // Log before navigation
      console.log("Navigating to Dashboard");
      navigate("/dashboard");
    } catch (err) {
      console.error("Login Error:", {
        error: err,
        response: err.response?.data
      });
      
      setError(
        err.response?.data?.message ||
        "Login failed. Please check your credentials and try again."
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="login-container flex justify-center items-center min-h-screen bg-gray-100">
      <div className="login-card w-full max-w-md bg-white shadow-lg rounded-xl p-8">
        {error && (
          <div 
            className="unauthorized-banner flex items-center bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded-r-lg shadow-md" 
            role="alert"
          >
            <AlertTriangle className="mr-4 text-yellow-500" size={24} />
            <span className="font-semibold text-sm">{error}</span>
          </div>
        )}
        <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Log in to Your Account</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label 
              htmlFor="username" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Username or Email
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              required
              aria-label="Username or Email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            /> 
          </div>
          <div className="mb-6">
            <label 
              htmlFor="password" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              aria-label="Password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading} 
            className="w-full bg-red-500 text-white py-2 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
          >
            {loading ? "Logging in..." : "Log In"}
          </Button>
        </form>
        <p className="text-center mt-4 text-sm text-gray-600">
          Don't have an account? {" "}
          <a 
            href="/signup" 
            className="text-blue-500 hover:text-blue-700 font-semibold"
          >
            Create an account
          </a>
        </p>
      </div>
    </div>
  );
}

export default Login;